import type { OrgStrengthTier } from "@/lib/orgProfileStrength";

/**
 * Whether to nudge an organization about its profile, and whether that nudge may
 * interrupt.
 *
 * Replaces the `profile_data.onboardingCompleted` gate. That flag was written
 * after six wizard steps and silenced everything permanently, at roughly a fifth
 * of a real profile — the system declared victory and stopped asking. Profile
 * strength decides now.
 *
 * Two softeners keep that from becoming nagging:
 *
 *   - A dismissal snoozes the AUTO-OPEN for a week. The banner stays; it is
 *     low-weight and already non-dismissible by design.
 *   - An organization that completed the OLD wizard is never auto-opened again,
 *     regardless of tier. They finished what they were asked to finish; being
 *     popped at afterwards reads as the product forgetting. They still see the
 *     banner, so the gap is visible without being insisted upon.
 */

export const AUTO_OPEN_SNOOZE_DAYS = 7;

export interface OrgOnboardingInput {
  tier: OrgStrengthTier;
  /** Legacy profile_data.onboardingCompleted — true for orgs that finished the old wizard. */
  legacyCompleted: boolean;
  /** ISO string, or null if never dismissed. */
  dismissedAt: string | null;
  now: Date;
}

export interface OrgOnboardingDecision {
  showBanner: boolean;
  allowAutoOpen: boolean;
}

/**
 * True when a dismissal is recent enough to still suppress the auto-open.
 *
 * Anything unparseable, or dated in the future, is not trusted: both would
 * otherwise suppress the wizard indefinitely, and a dismissal that has not
 * happened yet is not a dismissal.
 */
const isSnoozed = (dismissedAt: string | null, now: Date): boolean => {
  if (!dismissedAt) return false;
  const t = new Date(dismissedAt).getTime();
  if (!Number.isFinite(t)) return false;
  const elapsedDays = (now.getTime() - t) / 86_400_000;
  if (elapsedDays < 0) return false;
  return elapsedDays < AUTO_OPEN_SNOOZE_DAYS;
};

export const decideOrgOnboarding = ({
  tier,
  legacyCompleted,
  dismissedAt,
  now,
}: OrgOnboardingInput): OrgOnboardingDecision => {
  const needsWork = tier === "draft";
  if (!needsWork) return { showBanner: false, allowAutoOpen: false };

  return {
    showBanner: true,
    allowAutoOpen: !legacyCompleted && !isSnoozed(dismissedAt, now),
  };
};
